import type { ReactNode } from 'react';
import { StatusBadge } from '../ui/StatusBadge';
import { Alert } from '../ui/Alert';
import type { HtmlImportReport } from '../../types/api';

function formatBytes(n: number): string {
  if (n >= 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} bytes`;
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-1.5">
      <h3 className="m-0 text-xs font-semibold uppercase tracking-wide text-muted">{title}</h3>
      {children}
    </section>
  );
}

function Code({ children }: { children: ReactNode }) {
  return <code className="rounded bg-bg px-1 py-0.5 font-mono text-[12px]">{children}</code>;
}

const SENDER_TEXT = {
  merge_field: 'Sender details: added automatically from the brand kit.',
  written: 'Sender details: written into the HTML.',
  legal_notice: 'Sender details: found through a link to your own site or legal notice.',
  missing: 'Sender details: missing. The footer must say who is sending (e.g. your brand name).',
} as const;

/**
 * What the server found in an HTML newsletter, in plain words: what blocks
 * saving (each problem names what to do), what was translated, what the
 * sanitiser takes out, the images and the footer rules. Display only — it
 * never decides anything; the server checks again on every save.
 */
export function HtmlCheckReport({
  report,
  pending = false,
  imageAction = null,
}: {
  report: HtmlImportReport;
  pending?: boolean;
  /** Shown under the image summary: the "Copy all to NP-Flow" step. */
  imageAction?: ReactNode;
}) {
  const { images, footer } = report;
  const tooLarge = report.sizeBytes > report.maxBytes;
  const where = [
    images.base64 > 0 ? `${images.base64} embedded in the HTML` : null,
    images.mailchimpHosted > 0 ? `${images.mailchimpHosted} on Mailchimp's servers` : null,
    images.ownHosted > 0 ? `${images.ownHosted} on our own media domain` : null,
  ].filter((p): p is string => p !== null);
  return (
    <div className="flex flex-col gap-4 text-sm" aria-busy={pending}>
      <div className="flex items-center gap-2">
        {report.valid ? (
          <StatusBadge tone="success" icon="✓">
            Ready to save
          </StatusBadge>
        ) : (
          <StatusBadge tone="danger" icon="!">
            {report.problems.length} {report.problems.length === 1 ? 'problem' : 'problems'} to fix
          </StatusBadge>
        )}
        <span className="text-muted">
          {formatBytes(report.sizeBytes)}
          {tooLarge ? ` (limit ${formatBytes(report.maxBytes)})` : ''}
        </span>
        {pending ? <span className="text-muted">Checking…</span> : null}
      </div>

      {report.problems.length > 0 ? (
        <Alert>
          <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
            {report.problems.map((p, i) => (
              <li key={`${p.code}-${p.tag ?? ''}-${i}`}>
                {p.tag ? <Code>{p.tag}</Code> : null}
                {p.tag ? ' ' : ''}
                {p.message}
                {p.count && p.count > 1 ? ` (${p.count} times)` : ''}
              </li>
            ))}
          </ul>
        </Alert>
      ) : null}

      {report.translations.length > 0 ? (
        <Section title="Translated from Mailchimp">
          <ul className="m-0 flex list-none flex-col gap-1 p-0">
            {report.translations.map((t) => (
              <li key={t.tag}>
                <Code>{t.tag}</Code> becomes <Code>{t.to}</Code>
                {t.count > 1 ? ` (${t.count} times)` : ''}
              </li>
            ))}
          </ul>
        </Section>
      ) : null}

      {report.removed.length > 0 ? (
        <Section title="Removed when saved">
          <ul className="m-0 flex list-none flex-col gap-1 p-0">
            {report.removed.map((r) => (
              <li key={`${r.kind}-${r.name}`}>
                {r.kind === 'block' ? (
                  r.name
                ) : (
                  <Code>{r.kind === 'comment' ? `${r.name} comments` : r.name}</Code>
                )}
                {r.count > 1 ? ` ×${r.count}` : ''}
                {r.note ? <span className="text-muted">. {r.note}</span> : null}
              </li>
            ))}
          </ul>
        </Section>
      ) : null}

      <Section title="Images">
        {images.total === 0 ? (
          <p className="m-0 text-muted">No images.</p>
        ) : (
          <>
            <p className="m-0">
              {images.total} {images.total === 1 ? 'image' : 'images'}
              {where.length > 0 ? `, ${where.join(', ')}.` : '.'}
            </p>
            {images.hosts.length > 0 ? (
              <ul className="m-0 flex list-none flex-col gap-0.5 p-0 text-muted">
                {images.hosts.map((h) => (
                  <li key={h.host}>
                    <Code>{h.host}</Code> ×{h.count}
                    {h.mailchimp ? ' (Mailchimp)' : ''}
                  </li>
                ))}
              </ul>
            ) : null}
            {imageAction}
          </>
        )}
      </Section>

      <Section title="Footer">
        <ul className="m-0 flex list-none flex-col gap-1 p-0">
          <li>
            {footer.unsubscribeLink
              ? 'Unsubscribe link: found.'
              : 'Unsubscribe link: missing. Add an unsubscribe link to the footer.'}
          </li>
          <li>{SENDER_TEXT[footer.sender]}</li>
          <li>
            {footer.legalNotice
              ? 'Legal notice link: found.'
              : 'Legal notice link: none. Optional, but consider adding one.'}
          </li>
        </ul>
      </Section>
    </div>
  );
}
